/**
 * @author Simeon Artamonov <simeon@cross-works.co.uk>
 * @company Cross Works Ltd
 */
import { computed, type Ref } from 'vue'
import { getImageUrl, processDynamicZone } from './config'
import type {
  NavigationLink,
  HomepageCard,
  FeaturedMedia,
  SiteInfo,
  AboutContent,
  ContactInfo,
  Post,
  NewsListInfo,
  DesignContent,
  TechnologyContent,
  PageStyle,
  PaginatedResponse
} from './types'

export const createComputedProperties = (
  globalData: Ref<any>,
  navigationData: Ref<any>,
  homepageData: Ref<any>,
  aboutData: Ref<any>,
  contactData: Ref<any>,
  postsData: Ref<any>,
  paginatedPostsData: Ref<PaginatedResponse<any> | null>,
  newsListData: Ref<any>,
  designData: Ref<any>,
  technologyData: Ref<any>,
  seoData: Ref<any>
) => {
  // Computed - Navigation
  const navigation = computed((): NavigationLink[] => {
    if (!navigationData.value?.navigation?.menuItems) {
      return []
    }
    
    const menuItems = navigationData.value.navigation.menuItems
    
    const processedItems = menuItems.map((item: any) => {
      if (item.__typename === 'ComponentUtilsPageLink') {
        // Internal page link
        return {
          text: item.title || '',
          url: item.relatedPage?.slug ? (item.relatedPage.slug === 'home' ? '/' : `/${item.relatedPage.slug}`) : '/',
          isExternal: false
        }
      } else if (item.__typename === 'ComponentUtilsExternalLink') {
        // External link with custom text
        return {
          text: item.text || '',
          url: item.url || '/',
          isExternal: true
        }
      }
      return {
        text: '',
        url: '/',
        isExternal: false
      }
    })
    
    return processedItems
  })

  // Computed - Homepage Cards
  const homepageCards = computed((): HomepageCard[] => {
    if (!homepageData.value?.homepage?.cards?.card) {
      return []
    }
    
    const cardsData = homepageData.value.homepage.cards.card
    
    const processedCards = cardsData.map((card: any, index: number) => {
      return {
        id: card.id || index,
        heading: card.heading || '',
        description: card.description || '',
        thumbnail: card.thumb || null,
        blockAnchorID: card.blockAnchorID || null,
        link: {
          text: card.link?.text || '',
          url: card.link?.slug ? `/${card.link.slug}` : '/',
          isExternal: false,
          anchorId: card.link?.anchorID
        }
      }
    })
    
    return processedCards
  })

  // Computed - Homepage Cards Title
  const homepageCardsTitle = computed((): string => {
    if (!homepageData.value?.homepage?.cards) {
      return 'What we do'
    }
    
    const title = homepageData.value.homepage.cards.title || 'What we do'
    return title
  })

  // Computed - Featured Media
  const featuredMedia = computed((): FeaturedMedia | null => {
    const video = homepageData.value?.homepage?.video
    if (!video || !video.video?.videoId) return null
    
    return {
      url: `https://player.vimeo.com/video/${video.video.videoId}`,
      alt: 'Featured video'
    }
  })

  // Computed - Homepage Cover
  const homepageCover = computed(() => {
    const cover = homepageData.value?.homepage?.cover
    if (!cover) return null
    
    return {
      id: cover.id,
      showLogo: cover.logo || false,
      showTagline: cover.tagline !== false, // Default to true
      vimeoId: cover.background?.videoId || null,
      vimeoUrl: cover.background?.videoId ? `https://player.vimeo.com/video/${cover.background.videoId}` : null,
      background: cover.background || null
    }
  })

  // Computed - Homepage Style
  const homepageStyle = computed((): PageStyle | null => {
    const style = homepageData.value?.homepage?.Style
    if (!style) return null
    
    return {
      id: style.id,
      classNames: style.classNames,
      theme: style.theme,
      css: style.css,
      pageTopSpacer: style.pageTopSpacer
    }
  })

  // Computed - Homepage About Section
  const homepageAbout = computed(() => {
    const about = homepageData.value?.homepage?.about
    if (!about) return null
    
    return {
      id: about.id,
      title: about.title || '',
      description: about.description || '',
      textExtra: about.textExtra || '',
      button: about.button || null,
      profession: about.profession || []
    }
  })

  // Computed - Site Info
  const siteInfo = computed((): SiteInfo => {
    if (!globalData.value?.global) return {
      name: 'Cross Works',
      description: '',
      favicon: null,
      websitePassword: null
    }
    
    const global = globalData.value.global
    return {
      name: global.siteName || 'Cross Works',
      description: global.siteDescription || '',
      favicon: getImageUrl(global.favicon) || null,
      websitePassword: global.websitePassword || null
    }
  })

  // Computed - About Content
  const aboutContent = computed((): AboutContent | null => {
    if (!aboutData.value?.pages || !aboutData.value.pages.length) {
      return null
    }
    
    const aboutPage = aboutData.value.pages[0] // Get first matching page
    const rawContent = aboutPage.content || []
    const processedContent = processDynamicZone(rawContent)
    
    return {
      id: aboutPage.documentId || 'about',
      title: aboutPage.title || 'About',
      slug: aboutPage.slug || 'about',
      content: processedContent,
      pageStyle: aboutPage.pageStyle || null,
      password: aboutPage.password || null
    }
  })

  // Computed - Contact Content
  const contactContent = computed((): ContactInfo | null => {
    if (!contactData.value?.pages || !contactData.value.pages.length) {
      return null
    }
    
    const contactPage = contactData.value.pages[0] // Get first matching page
    const rawContent = contactPage.content || []
    const processedContent = processDynamicZone(rawContent)
    
    return {
      id: contactPage.documentId || 'contact',
      title: contactPage.title || 'Contact',
      slug: contactPage.slug || 'contact',
      content: processedContent,
      pageStyle: contactPage.pageStyle || null,
      password: contactPage.password || null
    }
  })

  // Computed - Posts/News
  const posts = computed((): Post[] => {
    const posts = postsData.value?.posts || []
    return posts.map((post: any): Post => ({
      id: post.documentId || post.id,
      title: post.title || 'Untitled',
      slug: post.slug || '',
      pageStyle: post.pageStyle || null,
      thumbnail: post.thumbnail,
      featuredImage: post.featuredImage,
      items: post.items || [],
      publishedAt: post.publishedAt,
      createdAt: post.createdAt,
      updatedAt: post.updatedAt,
      password: post.password
    }))
  })

  const paginatedPosts = computed((): Post[] => {
  const posts = paginatedPostsData.value?.nodes || []

  return posts
    .filter((post: any) => {
      const date = new Date(post.newsPublishedDate)
      return post.newsPublishedDate && !isNaN(date.getTime()) && date <= new Date()
    })
    .map((post: any): Post => ({
      id: post.documentId || post.id,
      title: post.title || 'Untitled',
      slug: post.slug || '',
      pageStyle: post.pageStyle || null,
      thumbnail: post.thumbnail,
      featuredImage: post.featuredImage,
      items: post.items || [],
      publishedAt: post.newsPublishedDate,
      createdAt: post.createdAt,
      updatedAt: post.updatedAt,
      password: post.password
    }))
})

  const postsPageInfo = computed(() => paginatedPostsData.value?.pageInfo || null)

  // Computed - News List Info
  const newsListInfo = computed((): NewsListInfo | null => {
    if (!newsListData.value?.pages) return null
    
    const newsList = newsListData.value.pages[0] // Get first matching page
    return {
      id: newsList.documentId,
      title: newsList.title || '',
      slug: newsList.slug || '',
      content: processDynamicZone(newsList.content || []),
      pageStyle: newsList.pageStyle || null,
      password: newsList.password || null
    }
  })

  // Computed - Design Content
  const designContent = computed((): DesignContent | null => {
    if (!designData.value?.pages || !designData.value.pages.length) {
      return null
    }
    
    const designPage = designData.value.pages[0] // Get first matching page
    const rawContent = designPage.content || []
    const processedContent = processDynamicZone(rawContent)
    
    return {
      id: designPage.documentId || 'design',
      title: designPage.title || 'Design',
      slug: designPage.slug || 'design',
      content: processedContent,
      pageStyle: designPage.pageStyle || null,
      password: designPage.password || null
    }
  })

  // Computed - Technology Content
  const technologyContent = computed((): TechnologyContent | null => {
    if (!technologyData.value?.pages || !technologyData.value.pages.length) {
      return null
    }
    
    const technologyPage = technologyData.value.pages[0] // Get first matching page
    const rawContent = technologyPage.content || []
    const processedContent = processDynamicZone(rawContent)
    
    return {
      id: technologyPage.documentId || 'technology',
      title: technologyPage.title || 'Technology',
      slug: technologyPage.slug || 'technology',
      content: processedContent,
      pageStyle: technologyPage.pageStyle || null,
      password: technologyPage.password || null
    }
  })

  // Computed - Offices
  const offices = computed(() => {
    if (!globalData.value?.global?.Offices) {
      return []
    }
    
    return globalData.value.global.Offices.map((office: any) => ({
      id: office.id,
      title: office.title || '',
      street: office.street || '',
      postcode: office.postcode || '',
      town: office.town || '',
      country: office.country || '',
      timezone: office.timezone || '',
      map: office.Map || null,
      email: office.email || []
    }))
  })

  // Computed - Global SEO
  const globalSeo = computed(() => {
    return seoData.value?.metaSEO || null
  })

  // Computed - Global Social Meta
  const globalSocials = computed(() => {
    return seoData.value?.metaSocials || null
  })

  return {
    navigation,
    homepageCards,
    homepageCardsTitle,
    featuredMedia,
    homepageCover,
    homepageStyle,
    homepageAbout,
    siteInfo,
    aboutContent,
    contactContent,
    posts,
    paginatedPosts,
    postsPageInfo,
    newsListInfo,
    designContent,
    technologyContent,
    offices,
    globalSeo,
    globalSocials
  }
}